/**
 * What the ground is like, region by region.
 *
 * This is the authoring surface for the two things travel reads. Both lists
 * come from the **server** rather than a copy here: they are the book's, and a
 * stale list in the client still renders as a confident set of options — the
 * same reason the verb costs are served.
 *
 * **Two vocabularies, side by side and never merged.** The book prints nine
 * kinds of *surroundings* for the getting-lost check and twenty kinds of
 * *terrain* for movement cost, and maps neither onto the other. Offering one
 * control for both would be inventing that mapping in the interface, which is
 * the worst place to invent one — it would look like the rules.
 */

import { useEffect, useState } from 'react';

import { api, ApiError } from '../api.js';

export interface Region {
  readonly regionId: string;
  readonly name: string;
  readonly travelTerrain: string | null;
  readonly surroundings: string | null;
  readonly obstacles: readonly string[];
  /** Table 10.6's populated-area addition. Null is the printed unpopulated baseline. */
  readonly populated: string | null;
}

export interface RegionVocabulary {
  readonly regions: readonly Region[];
  readonly terrains: readonly string[];
  readonly surroundings: readonly string[];
  readonly obstacles: readonly { readonly name: string; readonly cost: number; readonly note?: string }[];
  readonly populatedLevels: readonly string[];
}

const EMPTY: RegionVocabulary = {
  regions: [],
  terrains: [],
  surroundings: [],
  obstacles: [],
  populatedLevels: [],
};

export function RegionEditor({
  campaignId,
  onChanged,
}: {
  readonly campaignId: string;
  readonly onChanged?: () => void;
}) {
  const [vocab, setVocab] = useState<RegionVocabulary>(EMPTY);
  const [status, setStatus] = useState<string | null>(null);
  const [draft, setDraft] = useState({ regionId: '', name: '' });

  const load = () => {
    void api
      .get<RegionVocabulary>(`/api/campaigns/${campaignId}/regions`)
      .then((body) => setVocab({ ...EMPTY, ...body }))
      .catch(() => setStatus('could not load the regions'));
  };
  useEffect(load, [campaignId]);

  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  /**
   * Unnames a region.
   *
   * The route existed with no caller — fixed for anyone with curl and for
   * nobody using the product, which is the shape this panel exists to avoid.
   */
  const remove = (region: Region) => {
    if (pendingDelete !== region.regionId) {
      setPendingDelete(region.regionId);
      setStatus(`deleting ${region.name} also forgets that it was revealed`);
      return;
    }
    setPendingDelete(null);
    void api
      .delete(`/api/campaigns/${campaignId}/regions/${encodeURIComponent(region.regionId)}`)
      .then(() => {
        setStatus(`deleted ${region.name}`);
        load();
        onChanged?.();
      })
      .catch((e: unknown) => setStatus(e instanceof ApiError ? e.message : 'that was refused'));
  };

  const save = (region: Region, patch: Partial<Region>) => {
    const next = { ...region, ...patch };
    void api
      .put(`/api/campaigns/${campaignId}/regions/${encodeURIComponent(next.regionId)}`, {
        name: next.name,
        travelTerrain: next.travelTerrain ?? '',
        surroundings: next.surroundings ?? '',
        obstacles: next.obstacles,
        populated: next.populated ?? '',
      })
      .then(() => {
        setStatus(`saved ${next.name}`);
        load();
        onChanged?.();
      })
      // The server's own words: it names the list that was missed, and a
      // paraphrase here would be a second, worse copy of the rules.
      .catch((e: unknown) => setStatus(e instanceof ApiError ? e.message : 'that was refused'));
  };

  return (
    <div className="space-y-2 text-sm">
      {status && <p className="text-xs text-neutral-500 dark:text-neutral-400">{status}</p>}

      <div className="space-y-1">
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          Terrain decides how long a journey takes; surroundings decide the chance of getting
          lost. The book keeps them separate, so this does too.
        </p>
        <div className="flex flex-wrap gap-1">
          <input
            aria-label="New region id"
            value={draft.regionId}
            onChange={(e) => setDraft({ ...draft, regionId: e.target.value })}
            placeholder="lowlands"
            className="w-24 rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
          />
          <input
            aria-label="New region name"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="The Lowlands"
            className="min-w-0 flex-1 rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
          />
          <button
            type="button"
            disabled={draft.regionId.trim() === '' || draft.name.trim() === ''}
            onClick={() => {
              save(
                {
                  regionId: draft.regionId.trim(),
                  name: draft.name.trim(),
                  travelTerrain: null,
                  surroundings: null,
                  obstacles: [],
                  populated: null,
                },
                {},
              );
              setDraft({ regionId: '', name: '' });
            }}
            className="rounded-md border border-neutral-300 px-2 py-1 text-xs disabled:opacity-40 dark:border-neutral-700"
          >
            Add
          </button>
        </div>
      </div>

      <ul className="space-y-2">
        {vocab.regions.map((region) => (
          <li
            key={region.regionId}
            className="space-y-1 rounded-md border border-neutral-200 p-2 dark:border-neutral-800"
          >
            <span className="flex items-baseline gap-2 font-medium">
              <span>
                {region.name} <span className="text-xs text-neutral-500 dark:text-neutral-400">{region.regionId}</span>
              </span>
              {/* **Two presses, like every other irreversible act here.** The
                  first press states what it costs rather than asking "are you
                  sure?" — deleting also drops the region's fog reveal, so a
                  Owner who reuses the id later starts hidden again. */}
              <button
                type="button"
                onClick={() => remove(region)}
                className="ml-auto text-xs text-neutral-500 dark:text-neutral-400 underline"
              >
                {pendingDelete === region.regionId ? 'press again to delete' : 'delete'}
              </button>
            </span>

            <label className="block text-xs text-neutral-500 dark:text-neutral-400">
              Terrain — how long a mile takes
              <select
                aria-label={`Terrain for ${region.name}`}
                value={region.travelTerrain ?? ''}
                onChange={(e) => save(region, { travelTerrain: e.target.value || null })}
                className="mt-0.5 w-full rounded-md border border-neutral-300 bg-white px-1 py-0.5 text-xs dark:border-neutral-700 dark:bg-neutral-900"
              >
                {/* Unset is a real answer: travel then declines to guess. */}
                <option value="">not set — no travel time</option>
                {vocab.terrains.map((terrain) => (
                  <option key={terrain} value={terrain}>
                    {terrain}
                  </option>
                ))}
              </select>
            </label>

            <label className="block text-xs text-neutral-500 dark:text-neutral-400">
              Surroundings — the chance of getting lost
              <select
                aria-label={`Surroundings for ${region.name}`}
                value={region.surroundings ?? ''}
                onChange={(e) => save(region, { surroundings: e.target.value || null })}
                className="mt-0.5 w-full rounded-md border border-neutral-300 bg-white px-1 py-0.5 text-xs dark:border-neutral-700 dark:bg-neutral-900"
              >
                <option value="">not set — no lost check</option>
                {vocab.surroundings.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>

            <label className="block text-xs text-neutral-500 dark:text-neutral-400">
              How settled or patrolled this is — raises the encounter check
              <select
                aria-label={`Populated level for ${region.name}`}
                value={region.populated ?? ''}
                onChange={(e) => save(region, { populated: e.target.value || null })}
                className="mt-0.5 w-full rounded-md border border-neutral-300 bg-white px-1 py-0.5 text-xs dark:border-neutral-700 dark:bg-neutral-900"
              >
                {/* Unset is the printed baseline — "assumes an unpopulated wilderness". */}
                <option value="">unpopulated (the chart's own baseline)</option>
                {vocab.populatedLevels.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </label>

            <fieldset className="text-xs text-neutral-500 dark:text-neutral-400">
              <legend>In the way</legend>
              <div className="flex flex-wrap gap-x-2">
                {vocab.obstacles.map((obstacle) => (
                  <label key={obstacle.name} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={region.obstacles.includes(obstacle.name)}
                      onChange={(e) =>
                        save(region, {
                          obstacles: e.target.checked
                            ? [...region.obstacles, obstacle.name]
                            : region.obstacles.filter((name) => name !== obstacle.name),
                        })
                      }
                    />
                    {/* The cost is the whole of the difference between a ridge
                        and a chasm, so it is on the label rather than hidden. */}
                    <span title={obstacle.note ?? ''}>
                      {obstacle.name} <span className="text-neutral-400">+{obstacle.cost}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          </li>
        ))}
        {vocab.regions.length === 0 && (
          <li className="text-xs text-neutral-500 dark:text-neutral-400">
            No regions yet. Fog and waypoints already use region ids — naming one here is what
            lets travel cost the ground.
          </li>
        )}
      </ul>
    </div>
  );
}
